<template>
  <section
    class="relative w-screen -mx-[calc(50vw-50%)] bg-zinc-50 text-zinc-900 dark:bg-black dark:text-zinc-50 overflow-hidden">

    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute inset-0 transition-colors duration-500
               bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.12),transparent_60%)]
               dark:bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.18),transparent_60%)]"></div>
    </div>
    <div class="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20 min-h-screen flex flex-col">
      <header class="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div class="space-y-2">
          <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Palette library
          </p>
          <h2 class="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
            Curated color systems for modern products
          </h2>
          <p class="max-w-xl text-sm text-slate-600 dark:text-slate-300">
            Explore ready-made palettes tuned for interfaces, brands and marketing. Each one is
            balanced for contrast, mood and accessibility.
          </p>
        </div>
      </header>

      <div class="grid flex-1 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 pb-6">
        <article v-for="(p, index) in palettes" :key="p.name + index"
          class="group relative flex flex-col rounded-2xl border border-slate-900/5 bg-white/90 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.12)] backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_26px_70px_rgba(15,23,42,0.40)] dark:border-slate-100/10 dark:bg-slate-900/85">
          <div class="relative mb-3 flex gap-1.5">
            <div v-for="(c, idx) in p.colors" :key="p.name + '-swatch-' + idx"
              class="flex-1 h-16 rounded-xl shadow-[0_12px_30px_rgba(15,23,42,0.35)]" :style="{ background: c }"></div>
          </div>

          <div class="flex flex-1 flex-col gap-3">
            <div class="flex items-center justify-between gap-3">
              <div class="space-y-0.5">
                <p class="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Palette
                </p>
                <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-50">
                  {{ p.name }}
                </h3>
              </div>
              <span
                class="inline-flex items-center gap-1 rounded-full border border-slate-200/80 bg-white/90 px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-sm dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200">
                <span class="h-1.5 w-1.5 rounded-full" :class="p.tagColor"></span>
                {{ p.tag }}
              </span>
            </div>

            <p class="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {{ p.mood }}
            </p>

            <div class="mt-auto flex items-center justify-between pt-1 text-[11px] text-slate-500 dark:text-slate-400">
              <div class="flex flex-wrap items-center gap-1.5">
                <span class="rounded-full bg-slate-900/5 px-2 py-0.5 text-[10px] font-medium dark:bg-slate-50/5">
                  {{ p.context }}
                </span>
                <span class="rounded-full bg-slate-900/5 px-2 py-0.5 text-[10px] font-medium dark:bg-slate-50/5">
                  {{ p.surface }}
                </span>
              </div>
              <span class="flex items-center gap-1">
                <span class="flex h-4 w-7 items-center rounded-full bg-slate-900/5 px-[2px] dark:bg-slate-50/10">
                  <span
                    class="h-3 w-3 rounded-full bg-slate-900/80 transition-transform group-hover:translate-x-3 dark:bg-slate-50/80"></span>
                </span>
                {{ p.usage }}
              </span>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
const palettes = [
  {
    name: 'Aurora Interface',
    colors: ['#0F172A', '#0284C7', '#22C55E', '#FACC15'],
    tag: 'Interface',
    tagColor: 'bg-emerald-400',
    mood: 'Cool, vibrant and high-contrast for sharp dashboards and dense data surfaces.',
    context: 'UI Kits',
    surface: 'Dark / mixed',
    usage: 'Product'
  },
  {
    name: 'Nordic Frost',
    colors: ['#020617', '#0F172A', '#1D4ED8', '#38BDF8'],
    tag: 'Product',
    tagColor: 'bg-sky-400',
    mood: 'Crisp, technical palette tuned for focused tools and complex workflows.',
    context: 'SaaS',
    surface: 'Dark first',
    usage: 'Apps'
  },
  {
    name: 'Sunrise Deck',
    colors: ['#111827', '#7C3AED', '#EC4899', '#F59E0B'],
    tag: 'Marketing',
    tagColor: 'bg-pink-400',
    mood: 'Bold gradients for hero sections, pricing pages and launch campaigns.',
    context: 'Marketing',
    surface: 'Hero blocks',
    usage: 'Landing'
  },
  {
    name: 'Solaris Brand',
    colors: ['#022C22', '#0F766E', '#F97316', '#FDE68A'],
    tag: 'Branding',
    tagColor: 'bg-amber-400',
    mood: 'Grounded greens with warm highlights for confident, mature brands.',
    context: 'Identity',
    surface: 'Print + web',
    usage: 'Brand'
  },
  {
    name: 'Pastel Flow',
    colors: ['#F9A8D4', '#C4B5FD', '#A5F3FC', '#FDE68A'],
    tag: 'Product',
    tagColor: 'bg-violet-300',
    mood: 'Soft, friendly tones ideal for consumer products and onboarding flows.',
    context: 'Consumer',
    surface: 'Light UI',
    usage: 'Web'
  },
  {
    name: 'Celestial UI',
    colors: ['#020617', '#1D4ED8', '#38BDF8', '#A855F7'],
    tag: 'Product',
    tagColor: 'bg-indigo-400',
    mood: 'Cosmic blue-violet spectrum with strong depth and highlight hierarchy.',
    context: 'Design tools',
    surface: 'Dark UI',
    usage: 'SaaS'
  },
  {
    name: 'Noir Studio',
    colors: ['#020617', '#111827', '#4B5563', '#9CA3AF'],
    tag: 'Dark UI',
    tagColor: 'bg-slate-400',
    mood: 'Minimal and cinematic, perfect for media dashboards and pro tools.',
    context: 'Creator',
    surface: 'Monochrome',
    usage: 'Dashboards'
  },
  {
    name: 'Signal System',
    colors: ['#0F172A', '#22C55E', '#EF4444', '#F97316'],
    tag: 'Status',
    tagColor: 'bg-emerald-400',
    mood: 'Semantic color set for success, warning and error states.',
    context: 'Design tokens',
    surface: 'Components',
    usage: 'System'
  },
  {
    name: 'Neutral Grid',
    colors: ['#020617', '#111827', '#E5E7EB', '#F9FAFB'],
    tag: 'Base',
    tagColor: 'bg-slate-300',
    mood: 'Neutral framework for typography, layout and iconography.',
    context: 'Foundations',
    surface: 'Backgrounds',
    usage: 'System'
  }
]
</script>

<style scoped>
@keyframes pop-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

article {
  animation: pop-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}

article:nth-child(1) {
  animation-delay: 40ms;
}

article:nth-child(2) {
  animation-delay: 80ms;
}

article:nth-child(3) {
  animation-delay: 120ms;
}

article:nth-child(4) {
  animation-delay: 160ms;
}

article:nth-child(5) {
  animation-delay: 200ms;
}

article:nth-child(6) {
  animation-delay: 240ms;
}

article:nth-child(7) {
  animation-delay: 280ms;
}

article:nth-child(8) {
  animation-delay: 320ms;
}

article:nth-child(9) {
  animation-delay: 360ms;
}
</style>
